import {
  ArrowRight,
  BriefcaseBusiness,
  Building2,
  CircleCheckBig,
  Clock3,
  Handshake,
  Mail,
  MoveRight,
  RadioTower,
  UserRound,
  WalletCards,
} from "lucide-react";
import { Link } from "react-router";
import { useGetList } from "ra-core";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";

import { DashboardActivityLog } from "@/components/atomic-crm/dashboard/DashboardActivityLog";
import { DashboardStepper } from "@/components/atomic-crm/dashboard/DashboardStepper";
import { useConfigurationContext } from "@/components/atomic-crm/root/ConfigurationContext";
import type {
  Company,
  Contact,
  ContactNote,
  Deal,
  Task,
} from "@/components/atomic-crm/types";

const compactCurrency = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
  notation: "compact",
});

const compactNumber = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});

type MentoraXDashboardProps = {
  mobile?: boolean;
};

export const MentoraXDashboard = ({
  mobile = false,
}: MentoraXDashboardProps) => {
  const { dealStages } = useConfigurationContext();

  const contactsQuery = useGetList<Contact>("contacts", {
    pagination: { page: 1, perPage: 6 },
    sort: { field: "last_seen", order: "DESC" },
  });

  const companiesQuery = useGetList<Company>("companies", {
    pagination: { page: 1, perPage: 5 },
    sort: { field: "created_at", order: "DESC" },
  });

  const dealsQuery = useGetList<Deal>("deals", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "updated_at", order: "DESC" },
  });

  const tasksQuery = useGetList<Task>("tasks", {
    pagination: { page: 1, perPage: 30 },
    sort: { field: "due_date", order: "ASC" },
    filter: { "done_date@is": "null" },
  });

  const notesQuery = useGetList<ContactNote>("contact_notes", {
    pagination: { page: 1, perPage: 1 },
  });

  const isPending =
    contactsQuery.isPending ||
    companiesQuery.isPending ||
    dealsQuery.isPending ||
    tasksQuery.isPending ||
    notesQuery.isPending;

  if (isPending) {
    return null;
  }

  if (!contactsQuery.total) {
    return <DashboardStepper step={1} />;
  }

  if (!notesQuery.total) {
    return <DashboardStepper step={2} contactId={contactsQuery.data?.[0]?.id} />;
  }

  const deals = dealsQuery.data ?? [];
  const contacts = contactsQuery.data ?? [];
  const companies = companiesQuery.data ?? [];

  const activeDeals = deals.filter((deal) => !deal.archived_at);
  const liveValue = activeDeals.reduce((sum, deal) => sum + (deal.amount ?? 0), 0);
  const closedWon = deals.filter((deal) => deal.stage === "live-client").length;

  const now = new Date();
  const todayStr = now.toISOString().split("T")[0];
  const tomorrowStr = new Date(now.getTime() + 86_400_000).toISOString().split("T")[0];

  const taskPriority = (dueDate: string) => {
    const d = new Date(dueDate).toISOString().split("T")[0];
    if (d < todayStr) return 0;
    if (d === todayStr) return 1;
    if (d === tomorrowStr) return 2;
    return 3;
  };

  const tasks = (tasksQuery.data ?? [])
    .filter((t) => !t.done_date)
    .sort((a, b) => {
      const pa = taskPriority(a.due_date);
      const pb = taskPriority(b.due_date);
      if (pa !== pb) return pa - pb;
      return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
    })
    .slice(0, 6);

  const overdueTasks = tasks.filter((t) => taskPriority(t.due_date) === 0).length;

  const stageSnapshot = dealStages.map((stage) => {
    const dealsInStage = activeDeals.filter((deal) => deal.stage === stage.value);
    const stageValue = dealsInStage.reduce(
      (sum, deal) => sum + (deal.amount ?? 0),
      0,
    );

    return {
      label: stage.label,
      value: dealsInStage.length,
      amount: stageValue,
    };
  });

  const maxStageCount = Math.max(
    1,
    ...stageSnapshot.map((stage) => stage.value),
  );

  const shellClassName = mobile
    ? "space-y-4"
    : "space-y-6 lg:space-y-8";

  return (
    <div className={shellClassName}>
      {/* Final gradient stop was rgba(201,151,58,0.18) — a mostly-transparent gold
          that only read as "dark" because it sat on the dashboard's own dark
          background in dark mode. In light mode the page behind it is light, so
          the right edge washed out and swallowed the white text. Swapped for an
          opaque navy matching the left edge (rgba(18,23,33,0.98)) so the banner
          is dark navy end-to-end in both themes — dark mode's look is
          unaffected since that's almost exactly what showed through there
          already. */}
      <Card className="overflow-hidden border-0 bg-[radial-gradient(circle_at_top_left,_rgba(201,151,58,0.20),_transparent_34%),linear-gradient(135deg,_rgba(18,23,33,0.98),_rgba(25,32,45,0.95)_55%,_rgba(18,23,33,0.98))] text-white shadow-[0_30px_80px_-42px_rgba(15,23,42,0.8)]">
        <CardContent className={cn("px-6 py-6", mobile ? "space-y-6" : "px-8 py-8")}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="max-w-3xl space-y-4">
              <Badge className="border border-white/15 bg-white/8 text-[11px] uppercase tracking-[0.24em] text-white/80">
                MentoraX Revenue Command
              </Badge>
              <div className="space-y-3">
                <h2 className={cn("font-semibold tracking-tight", mobile ? "text-3xl" : "text-4xl")}>
                  Run buyer conversations, deal pressure, and follow-up rhythm from one surface.
                </h2>
                <p className="max-w-2xl text-sm leading-6 text-white/72 sm:text-base">
                  This front-end is tuned for MentoraX operations: qualification flow,
                  active pipeline, delivery handoffs, and account momentum in a single workspace.
                </p>
              </div>
            </div>
            <div className="grid min-w-[220px] gap-3 rounded-2xl border border-white/10 bg-white/6 p-4 backdrop-blur">
              <span className="text-xs uppercase tracking-[0.22em] text-white/60">
                This week
              </span>
              <MetricLine
                icon={Handshake}
                label="Open opportunities"
                value={compactNumber.format(activeDeals.length)}
              />
              <MetricLine
                icon={WalletCards}
                label="Pipeline value"
                value={compactCurrency.format(liveValue)}
              />
              <MetricLine
                icon={Clock3}
                label="Overdue actions"
                value={compactNumber.format(overdueTasks)}
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button
              asChild
              className="rounded-full bg-white text-slate-950 hover:bg-white/90"
            >
              <Link to="/contacts">
                Open contacts
                <ArrowRight />
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="rounded-full border-white/18 bg-white/6 text-white hover:bg-white/10 hover:text-white"
            >
              <Link to="/deals">
                Review pipeline
                <MoveRight />
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      <section
        className={cn(
          "grid gap-4",
          mobile ? "grid-cols-1" : "md:grid-cols-2 xl:grid-cols-4",
        )}
      >
        <MetricCard
          icon={UserRound}
          label="Relationships"
          value={compactNumber.format(contactsQuery.total ?? 0)}
          hint="Tracked contacts"
        />
        <MetricCard
          icon={Building2}
          label="Accounts"
          value={compactNumber.format(companiesQuery.total ?? 0)}
          hint="Company records"
        />
        <MetricCard
          icon={BriefcaseBusiness}
          label="Open deals"
          value={compactNumber.format(activeDeals.length)}
          hint={`${compactCurrency.format(liveValue)} in motion`}
        />
        <MetricCard
          icon={CircleCheckBig}
          label="Closed live"
          value={compactNumber.format(closedWon)}
          hint="Converted engagements"
        />
      </section>

      <section
        className={cn(
          "grid gap-4",
          mobile ? "grid-cols-1" : "xl:grid-cols-[1.2fr_0.8fr]",
        )}
      >
        <Card className="border-border/60 bg-card/92 shadow-[0_18px_60px_-40px_rgba(15,23,42,0.45)]">
          <CardHeader className="gap-3">
            <Badge variant="outline" className="rounded-full border-primary/25 bg-primary/8 text-primary">
              Pipeline shape
            </Badge>
            <div>
              <CardTitle className="text-xl">Stage pressure</CardTitle>
              <CardDescription>
                Where current opportunities are accumulating across the MentoraX buyer path.
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {stageSnapshot.map((stage) => (
              <div key={stage.label} className="space-y-2">
                <div className="flex items-center justify-between gap-4 text-sm">
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-foreground">{stage.label}</span>
                    <span className="text-muted-foreground">
                      {compactCurrency.format(stage.amount)}
                    </span>
                  </div>
                  <span className="rounded-full bg-secondary px-2.5 py-1 text-xs font-medium text-secondary-foreground">
                    {stage.value}
                  </span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-secondary/80">
                  <div
                    className="h-full rounded-full bg-[linear-gradient(90deg,_rgba(201,151,58,1),_rgba(18,23,33,0.85))]"
                    style={{
                      width: `${(stage.value / maxStageCount) * 100}%`,
                    }}
                  />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="border-border/60 bg-card/92 shadow-[0_18px_60px_-40px_rgba(15,23,42,0.45)]">
          <CardHeader className="gap-3">
            <Badge variant="outline" className="rounded-full border-primary/25 bg-primary/8 text-primary">
              Next actions
            </Badge>
            <div>
              <CardTitle className="text-xl">Operator queue</CardTitle>
              <CardDescription>
                Upcoming and overdue work pulled from task scheduling.
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {tasks.length ? (
              tasks.map((task) => {
                const isLate = taskPriority(task.due_date) === 0;

                return (
                  <div
                    key={task.id}
                    className="rounded-2xl border border-border/70 bg-background/75 p-4"
                  >
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <span className="text-sm font-medium text-foreground">
                        {task.text}
                      </span>
                      <Badge
                        variant="outline"
                        className={cn(
                          "rounded-full",
                          isLate && "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
                        )}
                      >
                        {isLate ? "Needs attention" : "Scheduled"}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-2">
                        <Clock3 className="h-3.5 w-3.5" />
                        {formatDate(task.due_date)}
                      </span>
                      <span className="capitalize">{task.type}</span>
                    </div>
                  </div>
                );
              })
            ) : (
              <EmptyState
                icon={RadioTower}
                title="No queued actions"
                description="Task scheduling is clear right now."
              />
            )}
          </CardContent>
        </Card>
      </section>

      <section
        className={cn(
          "grid gap-4",
          mobile ? "grid-cols-1" : "xl:grid-cols-[0.9fr_1.1fr]",
        )}
      >
        <Card className="border-border/60 bg-card/92 shadow-[0_18px_60px_-40px_rgba(15,23,42,0.45)]">
          <CardHeader className="gap-3">
            <Badge variant="outline" className="rounded-full border-primary/25 bg-primary/8 text-primary">
              Coverage
            </Badge>
            <div>
              <CardTitle className="text-xl">Recent relationships</CardTitle>
              <CardDescription>
                The last contacts seen across active conversations.
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {contacts.map((contact) => (
              <div
                key={contact.id}
                className="flex items-center justify-between gap-3 rounded-2xl border border-border/70 bg-background/75 p-4"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-foreground">
                    {contact.first_name} {contact.last_name}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">
                    {contact.title || "No role"}{contact.company_name ? ` • ${contact.company_name}` : ""}
                  </div>
                </div>
                <Badge variant="secondary" className="rounded-full capitalize">
                  {contact.status || "active"}
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="border-border/60 bg-card/92 shadow-[0_18px_60px_-40px_rgba(15,23,42,0.45)]">
          <CardHeader className="gap-3">
            <Badge variant="outline" className="rounded-full border-primary/25 bg-primary/8 text-primary">
              Signals
            </Badge>
            <div>
              <CardTitle className="text-xl">Activity pulse</CardTitle>
              <CardDescription>
                Notes, updates, and buyer movement happening inside the workspace.
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <CompactCallout
                icon={Mail}
                label="Recorded notes"
                value={compactNumber.format(notesQuery.total ?? 0)}
              />
              <CompactCallout
                icon={Building2}
                label="Accounts added"
                value={compactNumber.format(companiesQuery.total ?? 0)}
              />
            </div>

            <div className="space-y-3">
              {companies.length ? (
                companies.map((company) => (
                  <div
                    key={company.id}
                    className="flex items-center justify-between gap-3 rounded-2xl border border-border/70 bg-background/75 p-4"
                  >
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-foreground">
                        {company.name}
                      </div>
                      <div className="truncate text-xs text-muted-foreground">
                        {company.sector || "Unclassified sector"}
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {formatDate(company.created_at)}
                    </span>
                  </div>
                ))
              ) : (
                <EmptyState
                  icon={Building2}
                  title="No company records yet"
                  description="Accounts will appear here as the pipeline fills out."
                />
              )}
            </div>
          </CardContent>
        </Card>
      </section>

      <Card className="border-border/60 bg-card/92 shadow-[0_18px_60px_-40px_rgba(15,23,42,0.45)]">
        <CardHeader className="gap-3">
          <Badge variant="outline" className="rounded-full border-primary/25 bg-primary/8 text-primary">
            Timeline
          </Badge>
          <div>
            <CardTitle className="text-xl">Recent activity</CardTitle>
            <CardDescription>
              Live CRM events from notes, contacts, deals, and account changes.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <DashboardActivityLog />
        </CardContent>
      </Card>
    </div>
  );
};

const MetricCard = ({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: typeof UserRound;
  label: string;
  value: string;
  hint: string;
}) => (
  <Card className="border-border/60 bg-card/92 shadow-[0_18px_60px_-40px_rgba(15,23,42,0.45)]">
    <CardContent className="flex items-start justify-between gap-4 px-6 py-5">
      <div className="space-y-2">
        <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
          {label}
        </div>
        <div className="text-3xl font-semibold tracking-tight text-foreground">
          {value}
        </div>
        <div className="text-sm text-muted-foreground">{hint}</div>
      </div>
      <div className="rounded-2xl border border-primary/15 bg-primary/8 p-3 text-primary">
        <Icon className="h-5 w-5" />
      </div>
    </CardContent>
  </Card>
);

const MetricLine = ({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof UserRound;
  label: string;
  value: string;
}) => (
  <div className="flex items-center justify-between gap-3 text-sm">
    <span className="inline-flex items-center gap-2 text-white/70">
      <Icon className="h-4 w-4 text-[#C9973A]" />
      {label}
    </span>
    <span className="font-medium text-white">{value}</span>
  </div>
);

const CompactCallout = ({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof UserRound;
  label: string;
  value: string;
}) => (
  <div className="rounded-2xl border border-border/70 bg-background/75 p-4">
    <div className="mb-2 flex items-center gap-2 text-muted-foreground">
      <Icon className="h-4 w-4 text-primary" />
      <span className="text-xs uppercase tracking-[0.2em]">{label}</span>
    </div>
    <div className="text-2xl font-semibold tracking-tight text-foreground">{value}</div>
  </div>
);

const EmptyState = ({
  icon: Icon,
  title,
  description,
}: {
  icon: typeof UserRound;
  title: string;
  description: string;
}) => (
  <div className="rounded-2xl border border-dashed border-border bg-background/75 p-6 text-center">
    <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-secondary text-secondary-foreground">
      <Icon className="h-5 w-5" />
    </div>
    <div className="text-sm font-medium text-foreground">{title}</div>
    <div className="mt-1 text-sm text-muted-foreground">{description}</div>
  </div>
);

const formatDate = (value?: string) => {
  if (!value) {
    return "No date";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
  }).format(date);
};
